import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import { renderMarkdownLite } from '@/lib/markdown';
import type { Fiche, FicheColor, FicheSection } from '@pharmafiche/schema';
import { Fragment } from 'react';
import type { FicheEditHandlers } from './FicheDessinee';

interface FicheCliniqueProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

/**
 * Phase 6 — hospital protocol template.
 *
 * Pure white. Fraunces for display, Source Sans 3 for body. Black bar at the
 * top, "PROTOCOLE" caps label, then the title. Each section gets a 4px
 * colored left bar in its accent color and a thin hairline rule below the
 * heading. No card borders or shadows.
 */
export function FicheClinique({ fiche, editHandlers }: FicheCliniqueProps) {
  return (
    <div className="min-h-screen bg-white text-ink" style={{ WebkitFontSmoothing: 'antialiased' }}>
      {/* Top bar */}
      <div style={{ height: 12, background: '#1f2330' }} aria-hidden />
      <div className="max-w-[820px] mx-auto px-6 md:px-10 pt-6 pb-12 font-source">
        <header className="mb-8">
          <div className="font-source uppercase tracking-[0.18em] text-[11px] text-ink-soft mb-1">
            Protocole
          </div>
          <h1
            className="font-fraunces text-[36px] leading-tight font-bold text-ink"
            style={{ letterSpacing: '-0.01em' }}
          >
            <EditableText
              value={fiche.meta.title}
              plain
              ariaLabel="Titre"
              onCommit={(t) => editHandlers?.updateMetaTitle?.(t)}
            />
          </h1>
          {fiche.meta.subtitle ? (
            <p className="font-source text-[14px] text-ink-soft mt-2">{fiche.meta.subtitle}</p>
          ) : null}
        </header>

        <div className="flex flex-col gap-8">
          {fiche.sections.map((sec, i) => (
            <SectionRenderer key={sec.id} section={sec} num={i + 1} editHandlers={editHandlers} />
          ))}
        </div>

        <footer className="mt-12 pt-3 border-t border-ink/30 text-[11px] text-ink-soft flex items-center justify-between font-source">
          <span>Document généré par PharmaFiche · v{fiche.schema_version}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fiche.id.slice(0, 8)}</span>
        </footer>
      </div>
    </div>
  );
}

function colorOf(section: FicheSection): FicheColor {
  if (section.type === 'drug_card') return section.drug.color;
  if (section.type === 'intro' || section.type === 'classification') {
    return section.color;
  }
  if (section.type === 'warning') return 'red';
  if (section.type === 'clinical_case') return 'red';
  return 'neutral';
}

function sectionTitle(section: FicheSection): string {
  switch (section.type) {
    case 'intro':
    case 'classification':
    case 'comparison_table':
      return section.title;
    case 'drug_card':
      return section.drug.name;
    case 'clinical_case':
      return 'Cas clinique';
    case 'warning':
      return 'Avertissement';
    case 'mnemonic':
      return 'Mnémotechnique';
    case 'diagram':
      return 'Mécanisme';
    case 'free_text':
      return section.title ?? 'Note';
  }
}

function SectionRenderer({
  section,
  num,
  editHandlers,
}: {
  section: FicheSection;
  num: number;
  editHandlers?: FicheEditHandlers;
}) {
  const color = colorOf(section);
  const hex = FICHE_COLOR_HEX[color];

  return (
    <section
      data-section-id={section.id}
      className="relative pl-4"
      style={{ borderLeft: `4px solid ${hex}` }}
    >
      <h2 className="font-fraunces text-[20px] font-bold text-ink leading-tight mb-1">
        <span className="font-source text-[12px] uppercase tracking-[0.18em] text-ink-soft mr-2">
          {String(num).padStart(2, '0')}
        </span>
        {section.type === 'intro' ||
        section.type === 'classification' ||
        section.type === 'comparison_table' ? (
          <EditableText
            value={section.title}
            plain
            ariaLabel="Titre de section"
            onCommit={(t) => editHandlers?.updateSectionTitle?.(section.id, t)}
          />
        ) : section.type === 'drug_card' ? (
          <EditableText
            value={section.drug.name}
            plain
            ariaLabel="Nom de la molécule"
            onCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
          />
        ) : (
          sectionTitle(section)
        )}
      </h2>
      <hr className="border-0 border-t border-ink/15 mb-3" />

      {renderBody(section, hex, editHandlers)}
    </section>
  );
}

function renderBody(section: FicheSection, accent: string, editHandlers?: FicheEditHandlers) {
  if (section.type === 'intro' || section.type === 'classification') {
    return (
      <>
        <BulletList
          bullets={section.bullets}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
        {section.type === 'intro' && section.warning ? (
          <Warning accent={accent}>
            <EditableText
              value={section.warning}
              ariaLabel="Avertissement"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </Warning>
        ) : null}
        {section.type === 'classification' && section.note ? (
          <p className="font-source text-[14px] text-ink-soft mt-2 italic">
            <EditableText
              value={section.note}
              ariaLabel="Note"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </p>
        ) : null}
      </>
    );
  }

  if (section.type === 'drug_card') {
    return (
      <>
        <BulletList
          bullets={section.drug.intro_bullets}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {section.drug.sub_sections.map((ss) => (
            <Fragment key={ss.id}>
              <div>
                <div className="font-source uppercase tracking-[0.15em] text-[11px] text-ink-soft mb-1">
                  <EditableText
                    value={ss.label}
                    plain
                    ariaLabel="Étiquette sous-section"
                    onCommit={(t) =>
                      editHandlers?.updateDrugSubsectionLabel?.(section.id, ss.id, t)
                    }
                  />
                </div>
                <BulletList
                  bullets={ss.bullets}
                  onCommit={(id, t) =>
                    editHandlers?.updateDrugSubsectionBulletText?.(section.id, ss.id, id, t)
                  }
                />
              </div>
            </Fragment>
          ))}
        </div>
      </>
    );
  }

  if (section.type === 'clinical_case') {
    return (
      <>
        <p className="font-source text-[15px] leading-[1.55] text-ink mb-2">
          <EditableText
            value={section.patient}
            ariaLabel="Patient"
            onCommit={(t) => editHandlers?.updateClinicalPatient?.(section.id, t)}
          />
        </p>
        <div className="font-source text-[13px] uppercase tracking-[0.15em] text-ink-soft mt-2 mb-1">
          Prescription
        </div>
        <ul className="m-0 pl-4 list-disc">
          {section.prescription.map((p, i) => (
            <li key={`${section.id}-rx-${i}`} className="font-source text-[14px] leading-[1.55]">
              {renderMarkdownLite(p)}
            </li>
          ))}
        </ul>
        <p className="font-source text-[14px] mt-2">
          <strong>Question : </strong>
          <EditableText
            value={section.question}
            ariaLabel="Question"
            onCommit={(t) => editHandlers?.updateClinicalQuestion?.(section.id, t)}
          />
        </p>
        <div className="font-source text-[13px] uppercase tracking-[0.15em] text-ink-soft mt-3 mb-1">
          Conduite à tenir
        </div>
        <BulletList
          bullets={section.conduite}
          onCommit={(id, t) => editHandlers?.updateBulletText?.(section.id, id, t)}
        />
      </>
    );
  }

  if (section.type === 'mnemonic') {
    return (
      <p className="font-fraunces italic text-[18px] text-ink leading-[1.45]">
        <EditableText
          value={section.text}
          ariaLabel="Mnémo"
          onCommit={(t) => editHandlers?.updateMnemonicText?.(section.id, t)}
        />
      </p>
    );
  }

  if (section.type === 'warning') {
    return (
      <Warning accent="#d34a4a">
        <EditableText
          value={section.text}
          ariaLabel="Avertissement"
          onCommit={(t) => editHandlers?.updateWarningText?.(section.id, t)}
        />
      </Warning>
    );
  }

  if (section.type === 'diagram') {
    return (
      <p className="font-source text-[13px] text-ink-soft italic">
        Mécanisme — voir la version « Flowchart » de cette fiche pour le rendu graphique.
      </p>
    );
  }

  if (section.type === 'comparison_table') {
    return (
      <table
        className="w-full text-[13px] border-collapse mt-1"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <thead>
          <tr>
            <th className="font-source uppercase tracking-wider text-[11px] text-ink-soft text-left p-2">
              &nbsp;
            </th>
            {section.columns.map((c) => (
              <th key={c} className="font-fraunces font-bold text-left p-2 border-b-2 border-ink">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r, idx) => (
            <tr key={r.label} className={idx % 2 === 0 ? 'bg-paper/30' : ''}>
              <td className="font-source uppercase tracking-wider text-[11px] text-ink-soft p-2">
                {r.label}
              </td>
              {r.cells.map((c, j) => (
                <td key={`${r.label}-${j}`} className="font-source p-2 border-b border-ink/15">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (section.type === 'free_text') {
    return (
      <p className="font-source text-[15px] leading-[1.55] whitespace-pre-wrap">{section.body}</p>
    );
  }

  return null;
}

function BulletList({
  bullets,
  onCommit,
}: {
  bullets: { id: string; text: string }[];
  onCommit: (id: string, text: string) => void;
}) {
  return (
    <ul className="m-0 mt-1 pl-4 list-disc">
      {bullets.map((b) => (
        <li
          key={b.id}
          className="font-source text-[15px] leading-[1.55] text-ink my-[2px]"
          data-bullet-id={b.id}
        >
          <EditableText value={b.text} ariaLabel="Bullet" onCommit={(t) => onCommit(b.id, t)} />
        </li>
      ))}
    </ul>
  );
}

function Warning({ children, accent }: { children: React.ReactNode; accent: string }) {
  return (
    <p
      className="font-source text-[14px] mt-2 leading-[1.5] flex items-start gap-2"
      style={{ color: accent }}
    >
      <span
        aria-hidden
        className="inline-grid place-items-center font-bold text-white shrink-0"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: accent,
          fontSize: 12,
          lineHeight: 1,
        }}
      >
        !
      </span>
      <span>{children}</span>
    </p>
  );
}
